/**
 * Deal Financial Analysis Hub (SPRINT-15 F010)
 *
 * Wrapper component integrating all deal financial analysis panels (F005-F009)
 * into the Deal Detail page's Financials tab using sub-tab navigation.
 *
 * Sub-tabs:
 * - Configuration: Entity & Guarantor Config (F005)
 * - Consolidated: Consolidated Income Table w/ Intercompany Eliminations (F006)
 * - Serviceability: Dashboard (F008) + Inclusion Controls & DSCR (F007)
 * - Ratios: Financial Ratio Display (F009)
 * - Audit Trail: Calculations Summary (F009)
 *
 * URL param `?financialTab=<tab>` persists the active sub-tab across refreshes.
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Settings,
  BarChart3,
  Shield,
  PieChart,
  FileText,
  Activity,
  Plus,
  Building2,
  ScrollText,
  Download,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EntityGuarantorConfigPanel } from "./entity-guarantor-config";
import { SourceSelector } from "./source-selector";
import { DscrCalculator } from "./dscr-calculator";
import { ServiceabilityDashboard } from "./serviceability-dashboard";
import { RatioDisplay } from "./ratio-display";
import { CalculationsSummary } from "./calculations-summary";
import { SensitivityAnalysisPanel } from "./sensitivity-analysis-panel";
import { DealEbitdaAdjustmentsPanel } from "./deal-ebitda-adjustments-panel";
import { NotionalConsolidationView } from "./notional-consolidation-view";
import { CreditMemoList } from "./credit-memo-list";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DealFinancialHubProps {
  dealId: string;
}

interface AnalysisEntity {
  id: string;
  dealId: string;
  accountId: string;
  role: string;
  [key: string]: unknown;
}

interface AnalysisGuarantor {
  id: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitialTab(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("financialTab") || "configuration";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DealFinancialHub({ dealId }: DealFinancialHubProps) {
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [showTabs, setShowTabs] = useState(false);

  // SourceSelector -> DscrCalculator data bridge
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalCommitments, setTotalCommitments] = useState(0);

  const handleTotalsChange = useCallback(
    (income: number, commitments: number) => {
      setTotalIncome(income);
      setTotalCommitments(commitments);
    },
    [],
  );

  // Persist sub-tab to URL query param
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("financialTab") !== activeTab) {
      url.searchParams.set("financialTab", activeTab);
      window.history.replaceState({}, "", url.toString());
    }
  }, [activeTab]);

  // -----------------------------------------------------------------------
  // Status badge queries
  // -----------------------------------------------------------------------

  const { data: entities = [] } = useQuery<AnalysisEntity[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-entities`],
    enabled: !!dealId,
  });

  const { data: guarantors = [] } = useQuery<AnalysisGuarantor[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`],
    enabled: !!dealId,
  });

  const entityCount = entities.length;
  const guarantorCount = guarantors.length;
  const hasEntities = entityCount > 0;

  // -----------------------------------------------------------------------
  // Welcome card (first-time setup)
  // -----------------------------------------------------------------------

  if (!hasEntities && !showTabs) {
    return (
      <Card data-testid="financial-hub-welcome">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Set up your deal&apos;s financial analysis
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Configure entities, select financial periods, and review
              serviceability metrics for this deal.
            </p>
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                  1
                </div>
                Add entities
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                  2
                </div>
                Select periods
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                  3
                </div>
                Review serviceability
              </div>
            </div>
            <Button
              onClick={() => {
                setShowTabs(true);
                setActiveTab("configuration");
              }}
              data-testid="button-start-analysis"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start Analysis
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Tabbed hub
  // -----------------------------------------------------------------------

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      data-testid="financial-hub-tabs"
    >
      <TabsList className="w-full justify-start">
        <TabsTrigger
          value="configuration"
          className="gap-1.5"
          data-testid="tab-configuration"
        >
          <Settings className="h-3.5 w-3.5" />
          Configuration
          {entityCount > 0 && (
            <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
              {entityCount} {entityCount === 1 ? "entity" : "entities"}
              {guarantorCount > 0 &&
                `, ${guarantorCount} guarantor${guarantorCount !== 1 ? "s" : ""}`}
            </Badge>
          )}
        </TabsTrigger>

        <TabsTrigger
          value="consolidated"
          className="gap-1.5"
          data-testid="tab-consolidated"
        >
          <BarChart3 className="h-3.5 w-3.5" />
          Consolidated
        </TabsTrigger>

        <TabsTrigger
          value="serviceability"
          className="gap-1.5"
          data-testid="tab-serviceability"
        >
          <Shield className="h-3.5 w-3.5" />
          Serviceability
        </TabsTrigger>

        <TabsTrigger
          value="ratios"
          className="gap-1.5"
          data-testid="tab-ratios"
        >
          <PieChart className="h-3.5 w-3.5" />
          Ratios
        </TabsTrigger>

        <TabsTrigger
          value="audit-trail"
          className="gap-1.5"
          data-testid="tab-audit-trail"
        >
          <FileText className="h-3.5 w-3.5" />
          Audit Trail
        </TabsTrigger>

        <TabsTrigger
          value="sensitivity"
          className="gap-1.5"
          data-testid="tab-sensitivity"
        >
          <Activity className="h-3.5 w-3.5" />
          Sensitivity
        </TabsTrigger>

        <TabsTrigger
          value="credit-memo"
          className="gap-1.5"
          data-testid="tab-credit-memo"
        >
          <ScrollText className="h-3.5 w-3.5" />
          Credit Memo
        </TabsTrigger>
      </TabsList>

      {/* Configuration: Entity & Guarantor Config (F005) */}
      <TabsContent value="configuration" className="space-y-4 mt-4">
        <EntityGuarantorConfigPanel dealId={dealId} />
      </TabsContent>

      {/* Consolidated: Notional Consolidation Waterfall (F007) + EBITDA Adjustments CRUD (F006) + Export (F010) */}
      <TabsContent value="consolidated" className="space-y-4 mt-4">
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                data-testid="btn-export-analysis"
              >
                <Download className="h-3.5 w-3.5" />
                Export Analysis
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="export-option-consolidated"
                onClick={() => {
                  window.location.href = `/api/financial/deals/${dealId}/export/consolidated-analysis`;
                }}
              >
                Download as Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-option-sensitivity"
                onClick={() => {
                  window.location.href = `/api/financial/deals/${dealId}/export/sensitivity-analysis`;
                }}
              >
                Download Sensitivity Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <NotionalConsolidationView dealId={dealId} />
        <DealEbitdaAdjustmentsPanel dealId={dealId} />
      </TabsContent>

      {/* Serviceability: Dashboard (F008) + Inclusion Controls & DSCR (F007) */}
      <TabsContent value="serviceability" className="space-y-4 mt-4">
        <ServiceabilityDashboard dealId={dealId} />
        <SourceSelector dealId={dealId} onTotalsChange={handleTotalsChange} />
        <DscrCalculator
          dealId={dealId}
          totalIncome={totalIncome}
          totalCommitments={totalCommitments}
        />
      </TabsContent>

      {/* Ratios: Financial ratio display (F009) */}
      <TabsContent value="ratios" className="space-y-4 mt-4">
        <RatioDisplay dealId={dealId} />
      </TabsContent>

      {/* Audit Trail: Calculations summary (F009) */}
      <TabsContent value="audit-trail" className="space-y-4 mt-4">
        <CalculationsSummary dealId={dealId} />
      </TabsContent>

      {/* Sensitivity: Scenario stress testing (F005) */}
      <TabsContent value="sensitivity" className="space-y-4 mt-4">
        <SensitivityAnalysisPanel dealId={dealId} />
      </TabsContent>

      {/* Credit Memo: Memo list + editor (F008) */}
      <TabsContent value="credit-memo" className="space-y-4 mt-4">
        <CreditMemoList dealId={dealId} />
      </TabsContent>
    </Tabs>
  );
}
